import { propertyGroupTitleMap } from "../constants";
import { plural } from "pluralize";

export interface SplitData {
  splitType: string;
  splitValues: string[];
  isOther: boolean;
}

export interface PropertyGroup {
  splitDataChain: SplitData[];
  titleChain: string[];
  identity: string;
  index: number;
  verificationTemplateIdentifier: string | null;
  totalProperties: number;
}

interface TreeNode {
  level: number;
  splitType?: string;
  splitValues?: string[];
  propertyGroups: PropertyGroup[];
  isOther?: boolean;
  children: TreeNode[];
  id?: string;
  title?: string;
  totalProperties: number;
  verificationTemplateIdentifier?: string | null;
}

type NodeData = Omit<SplitData, "splitType"> & { title: string };

/**
 * A tree structure for organizing property groups based on split criteria.
 *
 * The GroupTree manages a hierarchical organization of property groups based on split types.
 * It provides functionality to build, manipulate, and transform the tree structure.
 *
 * Key features:
 * - Organizes property groups into a hierarchical tree structure
 * - Supports dynamic creation and modification of nodes
 * - Enables navigation through the tree via node IDs
 * - Provides transformation methods to convert between tree and flat data structures
 * - Maintains split chains and title chains for property grouping
 * - Handles "Other" categories automatically
 * - Tracks total property counts at each level
 */
export class GroupTree {
  private tree!: TreeNode;
  private splitBy: string[];

  constructor(splitBy: string[] = [], propertyGroups: PropertyGroup[] = []) {
    this.splitBy = splitBy;
    this.buildTree(propertyGroups);
  }

  setSplitBy(splitBy: string[]) {
    this.splitBy = splitBy;
  }

  getSplitBy() {
    return this.splitBy;
  }

  buildTree(propertyGroups: PropertyGroup[]) {
    if (propertyGroups.length === 0) return;

    this.tree = {
      level: 0,
      propertyGroups: propertyGroups,
      children: [],
      splitType: "",
      isOther: true,
      id: "root",
      title: "root",
      totalProperties: propertyGroups.reduce(
        (acc, curr) => acc + curr.totalProperties,
        0
      ),
      verificationTemplateIdentifier: null
    };

    const nodes = [[this.tree]];

    const maxLevel = this.splitBy.length;
    for (let currentLevel = 0; currentLevel < maxLevel; currentLevel++) {
      const currentNodes = nodes[currentLevel];

      for (const node of currentNodes) {
        const segmentGroups = new Map<
          string,
          {
            propertyGroups: PropertyGroup[];
            splitData: SplitData;
            title: string;
            verificationTemplateIdentifier: string | null;
          }
        >();

        for (const propertyGroup of node.propertyGroups) {
          const splitData = propertyGroup.splitDataChain[currentLevel + 1];
          const title = propertyGroup.titleChain[currentLevel + 1];

          if (!segmentGroups.has(title))
            segmentGroups.set(title, {
              propertyGroups: [],
              splitData: splitData,
              title: title,
              verificationTemplateIdentifier:
                propertyGroup.verificationTemplateIdentifier
            });

          segmentGroups.get(title)!.propertyGroups.push(propertyGroup);
        }

        for (const [key, group] of segmentGroups.entries()) {
          const nodeId = this.generateNodeId(node.id || "root", key);
          const { splitData, title, verificationTemplateIdentifier } = group;

          const childNode: TreeNode = {
            ...splitData,
            level: currentLevel + 1,
            title: title,
            propertyGroups: group.propertyGroups,
            children: [],
            id: nodeId,
            totalProperties: group.propertyGroups.reduce(
              (acc, curr) => acc + curr.totalProperties,
              0
            ),
            verificationTemplateIdentifier: verificationTemplateIdentifier
          };

          node.children.push(childNode);
        }
      }

      const nextLevelNodes = currentNodes.map((node) => node.children).flat();
      nodes.push(nextLevelNodes);
    }
  }

  addNode(parentNode: TreeNode, splitData: NodeData): TreeNode | undefined {
    if (parentNode.level === this.maxHeight) return;

    const nodeLevel = parentNode.level + 1;
    const nodeSplitType = this.splitBy[nodeLevel - 1];
    const nodeId = this.generateNodeId(parentNode.id, splitData.title);

    const newNode: TreeNode = {
      ...splitData,
      splitType: nodeSplitType,
      level: nodeLevel,
      propertyGroups: [],
      children: [],
      id: nodeId,
      totalProperties: 0,
      verificationTemplateIdentifier: parentNode.verificationTemplateIdentifier
    };

    parentNode.children.push(newNode);

    const createChildren = (parentNode: TreeNode) => {
      if (parentNode.level === this.maxHeight) return;

      const nodeLevel = parentNode.level + 1;
      const nodeSplitType = this.splitBy[nodeLevel - 1];
      const nodeId = this.generateNodeId(
        parentNode.id,
        `Other ${plural(propertyGroupTitleMap[nodeSplitType] || "")}`
      );

      const newNode: TreeNode = {
        title: `Other ${plural(propertyGroupTitleMap[nodeSplitType] || "")}`,
        splitType: nodeSplitType,
        splitValues: [],
        isOther: true,
        level: nodeLevel,
        propertyGroups: [],
        children: [],
        id: nodeId,
        totalProperties: 0,
        verificationTemplateIdentifier:
          parentNode.verificationTemplateIdentifier
      };

      parentNode.children.push(newNode);

      createChildren(newNode);
    };

    createChildren(newNode);

    return newNode;
  }

  updateChildren(nodeId: string, splitDatas: NodeData[]) {
    const node = this.getNode(nodeId);
    if (!node) return;

    const existingChildren = [...node.children];
    const result: TreeNode[] = [];

    for (let i = 0; i < splitDatas.length; i++) {
      const splitData = splitDatas[i];
      const existingChild =
        i < existingChildren.length ? existingChildren[i] : null;

      if (existingChild) {
        if (existingChild.title !== splitData.title) {
          existingChild.title = splitData.title;
          existingChild.id = this.generateNodeId(node.id, splitData.title);
          this.updateDescendantIdsRecursively(existingChild);
        }

        existingChild.splitValues = splitData.splitValues;
        existingChild.isOther = splitData.isOther;
        result.push(existingChild);
      } else {
        const newNode = this.addNode(node, splitData);
        if (newNode) result.push(newNode);
      }
    }

    node.children = result;
  }

  getTree(): TreeNode {
    return this.tree;
  }

  getNode(nodeId: string): TreeNode | undefined {
    const queue: TreeNode[] = [this.tree];

    while (queue.length > 0) {
      const currentNode = queue.shift();
      if (currentNode && currentNode.id) {
        if (currentNode.id === nodeId) return currentNode;
        if (nodeId.startsWith(currentNode.id))
          queue.push(...currentNode.children);
      }
    }

    return undefined;
  }

  transformToPropertyGroups(): PropertyGroup[] {
    if (this.tree.children.length === 0) return [];

    let index = 0;
    const propertyGroups: PropertyGroup[] = [];

    const processNode = (
      node: TreeNode,
      splitDataChain: SplitData[],
      titleChain: string[]
    ) => {
      const currentSplitDataChain = [
        ...splitDataChain,
        {
          splitType: node.splitType || "",
          splitValues: node.splitValues || [],
          isOther: node.isOther || false
        }
      ];
      const currentTitleChain = [...titleChain, node.title || ""];

      if (node.level === this.maxHeight) {
        index++;
        propertyGroups.push({
          splitDataChain: currentSplitDataChain,
          titleChain: currentTitleChain,
          identity: `pg_${node.id}`,
          index: index,
          verificationTemplateIdentifier:
            node.verificationTemplateIdentifier || null,
          totalProperties: node.totalProperties
        });
      } else
        for (const child of node.children)
          processNode(child, currentSplitDataChain, currentTitleChain);
    };

    processNode(this.tree, [], []);

    return propertyGroups;
  }

  transformToNodes() {
    const nodes: any = [];
    let index = 0;

    const processNode = (node: TreeNode, splitDataChain: SplitData[]) => {
      index++;
      const currentSplitDataChain = [
        ...splitDataChain,
        {
          splitType: node.splitType || "",
          splitValues: node.splitValues || [],
          isOther: node.isOther || false
        }
      ];

      nodes.push({
        title: node.title,
        splitDataChain: currentSplitDataChain,
        identity: node.id,
        index: index,
        total_properties: node.totalProperties
      });

      for (const child of node.children)
        processNode(child, currentSplitDataChain);
    };

    processNode(this.tree, []);

    return nodes;
  }

  private get maxHeight(): number {
    return this.splitBy.length;
  }

  private generateNodeId(parentId: string = "root", key: string): string {
    return `${parentId}_${key}`;
  }

  private updateDescendantIdsRecursively(node: TreeNode): void {
    for (const child of node.children) {
      child.id = this.generateNodeId(node.id, child.title || "");
      this.updateDescendantIdsRecursively(child);
    }
  }
}
